import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SkipForward } from "lucide-react";
import { SRT_HERO_INTRO, SRT_BRAND_MARK } from "@/lib/assets";

/**
 * Landing hero intro. Fullscreen video plays over the page on mount,
 * then fades out revealing the Landing hero underneath.
 *   - Muted + playsInline so mobile autoplay works.
 *   - Auto-advance either when the video ends or after MAX_MS.
 *   - Skip button (always visible) — users hate being trapped.
 *   - After fade-out, calls onDone so parent can animate the static logo in.
 */
const MAX_MS = 4200; // hard cutoff even if the file is longer

export default function HeroIntro({ onDone }) {
    const videoRef = useRef(null);
    const [phase, setPhase] = useState("playing"); // "playing" | "morphing" | "gone"

    // Hard cutoff timer
    useEffect(() => {
        const t = setTimeout(() => advance(), MAX_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function advance() {
        setPhase((p) => (p === "playing" ? "morphing" : p));
    }

    // Once the exit animation of the video finishes, tell the parent.
    function handleMorphComplete() {
        setPhase("gone");
        onDone?.();
    }

    if (phase === "gone") return null;

    return (
        <AnimatePresence>
            <motion.div
                key="hero-intro"
                data-testid="hero-intro"
                className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                style={{ background: "#000" }}
            >
                {/* Radial red haze — feels engine-y */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, rgba(225,6,0,0.18) 0%, transparent 55%)",
                    }}
                />

                {phase === "playing" && (
                    <motion.video
                        ref={videoRef}
                        data-testid="hero-intro-video"
                        src={SRT_HERO_INTRO}
                        autoPlay
                        muted
                        playsInline
                        preload="auto"
                        onEnded={advance}
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        style={{
                            filter:
                                "drop-shadow(0 30px 80px rgba(225,6,0,0.35)) drop-shadow(0 0 60px rgba(0,0,0,0.9))",
                        }}
                    />
                )}

                {/* Morph frame: static logo blooms into center as video fades */}
                {phase === "morphing" && (
                    <motion.img
                        key="morph-logo"
                        src={SRT_BRAND_MARK}
                        alt="SRT Finder"
                        initial={{ opacity: 0, scale: 0.6, filter: "blur(14px)" }}
                        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        exit={{ opacity: 0, scale: 0.92 }}
                        transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                        onAnimationComplete={() => {
                            // Hold the logo on-screen for a beat before revealing the page
                            setTimeout(handleMorphComplete, 550);
                        }}
                        draggable={false}
                        className="max-w-[80vw] max-h-[80vh] w-auto h-auto object-contain select-none"
                        style={{
                            filter:
                                "drop-shadow(0 30px 60px rgba(225,6,0,0.45)) drop-shadow(0 0 40px rgba(0,0,0,0.85))",
                        }}
                    />
                )}

                {/* Skip control — always available */}
                <button
                    type="button"
                    data-testid="hero-intro-skip"
                    onClick={advance}
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold text-white"
                    style={{
                        background: "rgba(20,20,20,0.75)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        borderRadius: 6,
                        backdropFilter: "blur(10px)",
                        letterSpacing: "0.16em",
                    }}
                    aria-label="Skip intro"
                >
                    SKIP <SkipForward className="w-3.5 h-3.5" />
                </button>

                {/* Bottom scan-line detail */}
                <div
                    aria-hidden
                    className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(180deg, transparent 0%, rgba(225,6,0,0.08) 60%, rgba(0,0,0,0.6) 100%)",
                    }}
                />
            </motion.div>
        </AnimatePresence>
    );
}
